export type CheckGridSubjectType = "resource" | "task" | "client";
export type CheckGridRecurrence = "none" | "daily" | "weekly" | "monthly";
export type CheckGridDisplayState = "expected" | "incomplete" | "missed" | "satisfied" | "not_applicable";
export type CheckGridAttention = "normal" | "attention_needed";
export type CheckGridPhase = "past" | "current" | "future" | string;
export type CheckGridApplicability = "applicable" | "not_applicable";
export type CheckGridCellPatch = {
  checked?: boolean;
  comment?: string | null;
  applicability?: CheckGridApplicability;
  /** Person the check is for (matrix column). Omit to tick the caller. */
  board_user_id?: number;
};
export type CheckgridEvidence = { thumbnail: string; full: string };
export type CheckGridSatisfaction = "pending" | "satisfied" | string;

export type CheckGridSummary = {
  subject_count: number;
  item_count: number;
  /** Required checks in the window (excludes not_required / expected). */
  applicable: number;
  unresolved: number;
  missed: number;
  attention_needed: number;
  last_activity: string | null;
};

export type CheckGridOverviewItem = {
  id: number;
  name: string;
  description?: string | null;
  subject_type: CheckGridSubjectType;
  recurrence: CheckGridRecurrence;
  recurrence_weekdays?: number[];
  archived: boolean;
  can_view_grant?: number;
  can_edit_grant?: number;
  source?: string | null;
  created_at?: string;
  summary: CheckGridSummary;
  /** Checklist host entity (new checkgrids list shape). */
  resource_id?: number | null;
  task_id?: number | null;
  client_id?: number | null;
  assigned_boarduser_ids?: number[];
  /** Name of the host task / resource / client. */
  entity_name?: string | null;
};

export type CheckGridTemplateItem = {
  id: number;
  key: string;
  description: string;
  order: number;
  archived?: boolean;
  comment?: string | null;
  image_url?: string | null;
  thumbnail_url?: string | null;
};

export type CheckGridSubject = {
  subject_id: number;
  checklist_id: number;
  name: string;
  resource_id?: number | null;
  task_id?: number | null;
  client_id?: number | null;
};

export type CheckGridDefinition = {
  id: number;
  name: string;
  description?: string | null;
  subject_type: CheckGridSubjectType;
  recurrence: CheckGridRecurrence;
  recurrence_weekdays?: number[];
  archived: boolean;
  can_view_grant?: number;
  can_edit_grant?: number;
  source?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  creator_name?: string | null;
  updater_name?: string | null;
  resource_id?: number | null;
  task_id?: number | null;
  client_id?: number | null;
  items: CheckGridTemplateItem[];
  subjects: CheckGridSubject[];
};

export type CheckGridMatrixCell = {
  subject_id: number;
  item_id: number;
  checklist_id: number | null;
  checklist_item_id: number | null;
  recorded_at: string | null;
  recorded_by_id?: number | null;
  description_snapshot?: string | null;
  applicability: CheckGridApplicability;
  satisfaction: CheckGridSatisfaction;
  checked: boolean;
  checked_by?: string | null;
  comment?: string | null;
  comment_by?: string | null;
  display_state: CheckGridDisplayState;
  attention: CheckGridAttention;
  has_evidence: boolean;
};

export type CheckGridCellHistoryEntry = {
  id: number;
  recorded_at: string;
  checked: boolean;
  applicability: CheckGridApplicability;
  comment: string | null;
  comment_by: string | null;
  checked_by: string | null;
  description_snapshot?: string | null;
  has_evidence?: boolean;
};

/** Sentinel subject when a checklist has no assignees — ticks omit `board_user_id`. */
export const UNASSIGNED_SUBJECT_ID = 0;

export type CheckGridMatrix = {
  checklist_id: number;
  /** Always true once adapted for the UI (0/1/many assignees all show a grid). */
  available: boolean;
  from: string;
  to: string;
  as_of: string;
  phase: CheckGridPhase;
  subjects: CheckGridSubject[];
  items: CheckGridTemplateItem[];
  cells: CheckGridMatrixCell[];
  summary: CheckGridSummary;
};

export type OverviewLaneKey = "attention" | "progress" | "settled";

export type OverviewLane = {
  key: OverviewLaneKey;
  title: string;
  grids: CheckGridOverviewItem[];
};
